import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Swal from "../utils/apparifySwal";
import { ArrowLeft, Trash2 } from "lucide-react";
import { getApplicationForEdit, resubmitApplication } from "../services/notificationService";
import { getBusinessApplicationDocumentUrl } from "../services/api";
import { uploadBusinessApplicationDocumentAsset } from "../services/assetUploadService";
import { maskEmail, maskPhone } from "../features/account/masking";
import { fmtFileSize as formatFileSize } from "../utils/formatters";
import {
  BUSINESS_APPLICATION_TERMS_ACKNOWLEDGEMENT_LABEL,
  BUSINESS_APPLICATION_TERMS_BODY,
  BUSINESS_APPLICATION_TERMS_REQUIRED_MESSAGE,
  BUSINESS_APPLICATION_TERMS_TITLE,
} from "../utils/businessApplicationTerms";
import { FormSkeleton } from "../components/skeletons/ShopperSkeletons";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_RE  = /^[a-zA-Z .'-]{2,80}$/;
const CITY_RE  = /^[a-zA-Z .'-]{2,60}$/;
const PHONE_RE = /^09\d{9}$/;
const POSTAL_RE = /^\d{4,6}$/;

const MAX_FILE_MB_PERMIT = 5;
const MAX_FILE_MB_ADDITIONAL = 10;
const MAX_ADDITIONAL_DOCS = 10;
const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/png"];

const DOC_TARGET_OPTIONS = [
  { value: "permit", label: "DTI/SEC Permit" },
  { value: "additional_documents", label: "Additional Document" },
];

const DOC_FIELD_TO_DB = {
  permit: "dti_sec_permit",
  additional_documents: "additional_document",
};

const DOC_DB_TO_UPLOAD = {
  dti_sec_permit: "permit",
  additional_document: "additional_documents",
};

const DOC_LABELS = {
  dti_sec_permit: "DTI/SEC Permit",
  additional_document: "Additional Document",
};

const REQUIRED_DOC_FIELDS = ["dti_sec_permit"];
const getMaxFileMbForTarget = (targetField) => (
  targetField === "permit" ? MAX_FILE_MB_PERMIT : MAX_FILE_MB_ADDITIONAL
);

const clampLen = (str, max) => String(str || "").slice(0, max);
const isImageType = (f) => f?.type === "image/jpeg" || f?.type === "image/png";
const isImageMime = (m) => String(m || "").startsWith("image/");

const extensionFromName = (name = "") => {
  const parts = String(name).split(".");
  return parts.length > 1 ? parts.pop().toUpperCase() : "DOC";
};

const getExistingDocName = (doc) => (
  doc?.original_filename || doc?.stored_filename || doc?.file_key?.split("/").pop() || `Document #${doc?.id || ""}`
);

const getExistingDocType = (doc) => {
  if (doc?.content_type) {
    if (doc.content_type === "application/pdf") return "PDF";
    if (doc.content_type === "image/png") return "PNG Image";
    if (doc.content_type === "image/jpeg") return "JPG Image";
  }
  return extensionFromName(getExistingDocName(doc));
};

const normalizeValue = (value) => String(value ?? "").trim();

const FIELD_LABELS = {
  ownerFullName: "Owner name",
  ownerEmail: "Owner email",
  ownerPhone: "Owner phone",
  businessName: "Business name",
  registrationNumber: "Registration no.",
  taxId: "Tax ID / TIN",
  businessEmail: "Business email",
  businessPhone: "Business phone",
  addressLine1: "Address line 1",
  addressLine2: "Address line 2",
  city: "City",
  province: "Province",
  postalCode: "Postal code",
};

export default function ResubmitApplication() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [pageState, setPageState] = useState("loading"); // loading | ready | error | notfound
  const [loadMessage, setLoadMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [app, setApp] = useState(null);  // original application data

  const [form, setForm] = useState({
    ownerFullName: "", ownerEmail: "", ownerPhone: "",
    businessName: "", registrationNumber: "", taxId: "",
    businessEmail: "", businessPhone: "",
    addressLine1: "", addressLine2: "", city: "", province: "", postalCode: "",
    applicantNotes: "",
    agree: false,
  });

  // Existing docs from API (may be marked for removal)
  const [existingDocs, setExistingDocs] = useState([]);   // [{id, label, file_path, ...}]
  const [removedDocIds, setRemovedDocIds] = useState([]);  // ids to supersede
  // New files to upload
  const [newDocs, setNewDocs] = useState([]); // [{ file, targetField, replacesDocId? }]
  const [switchTarget, setSwitchTarget] = useState(null); // { docId, targetField }
  const addDocsInputRef = useRef(null);
  const switchInputRef = useRef(null);

  /* ── Fetch application data ── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getApplicationForEdit(id);
        if (cancelled) return;
        const d = res.data;
        setApp(d);
        setForm({
          ownerFullName: [d.owner_first_name, d.owner_last_name].filter(Boolean).join(" "),
          ownerEmail: d.owner_email || "",
          ownerPhone: d.owner_phone || "",
          businessName: d.business_name || "",
          registrationNumber: d.registration_number || "",
          taxId: d.tax_id || "",
          businessEmail: d.business_email || "",
          businessPhone: d.business_phone || "",
          addressLine1: d.address_line1 || "",
          addressLine2: d.address_line2 || "",
          city: d.city || "",
          province: d.province || "",
          postalCode: d.postal_code || "",
          applicantNotes: "",
          agree: false,
        });
        setExistingDocs(d.documents || []);
        setPageState("ready");
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        const msg = err?.response?.data?.msg || "";
        setLoadMessage(msg);
        setPageState(status === 404 || status === 403 ? "notfound" : "error");
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const swalBase = useMemo(() => ({}), []);

  const inputBase  = "w-full rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 outline-none transition focus:border-neutral-400 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-50 dark:placeholder:text-neutral-500 dark:focus:border-neutral-600";
  const labelBase  = "mb-2 block text-xs tracking-wide text-neutral-600 dark:text-neutral-300";
  const sectionTitle = "text-xs font-semibold tracking-[0.35em] text-neutral-500 dark:text-neutral-400";
  const Req = () => <span className="ml-1 align-top text-red-600 dark:text-red-500">*</span>;

  const allowChars = (value, re) => {
    let out = "";
    for (const ch of String(value || "")) { if (re.test(ch)) out += ch; }
    return out;
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setForm((p) => ({ ...p, [name]: checked }));
      return;
    }
    let v = value;
    if (name === "ownerFullName") { v = clampLen(allowChars(v, /[a-zA-Z .'-]/), 80); }
    else if (name === "businessName") { v = clampLen(allowChars(v, /[a-zA-Z0-9 .,'&()-]/), 120); }
    else if (name === "city" || name === "province") { v = clampLen(allowChars(v, /[a-zA-Z ]/), 60); }
    else if (name === "ownerPhone" || name === "businessPhone") { v = clampLen(String(v).replace(/\D/g, ""), 11); }
    else if (name === "postalCode") { v = clampLen(String(v).replace(/\D/g, ""), 6); }
    else if (name === "registrationNumber") { v = clampLen(allowChars(v, /[a-zA-Z0-9\- ]/), 40); }
    else if (name === "taxId") { v = clampLen(String(v).replace(/[^\d-]/g, ""), 15); }
    else if (name === "ownerEmail" || name === "businessEmail") { v = clampLen(v, 120); }
    else if (name === "addressLine1" || name === "addressLine2") { v = clampLen(v, 140); }
    else if (name === "applicantNotes") { v = clampLen(v, 600); }
    setForm((p) => ({ ...p, [name]: v }));
  };

  const getProjectedActiveDocCount = useCallback((nextDocs = newDocs, nextRemovedIds = removedDocIds) => {
    const nextActiveExisting = existingDocs.filter((d) => !nextRemovedIds.includes(d.id));
    const netNewAdds = nextDocs.filter((item) => !item.replacesDocId).length;
    return nextActiveExisting.length + netNewAdds;
  }, [existingDocs, newDocs, removedDocIds]);

  const upsertPendingDoc = useCallback((docConfig) => {
    setNewDocs((prev) => {
      if (docConfig.replacesDocId) {
        const existingIdx = prev.findIndex((item) => Number(item.replacesDocId) === Number(docConfig.replacesDocId));
        if (existingIdx >= 0) {
          return prev.map((item, idx) => (idx === existingIdx ? docConfig : item));
        }
      }

      if (!docConfig.replacesDocId && docConfig.targetField !== "additional_documents") {
        const existingIdx = prev.findIndex(
          (item) => !item.replacesDocId && item.targetField === docConfig.targetField
        );
        if (existingIdx >= 0) {
          return prev.map((item, idx) => (idx === existingIdx ? docConfig : item));
        }
      }

      return [...prev, docConfig];
    });

    if (docConfig.replacesDocId) {
      setRemovedDocIds((prev) => prev.filter((idVal) => Number(idVal) !== Number(docConfig.replacesDocId)));
    }
  }, []);

  const onNewDocsChange = (e) => {
    const chosen = Array.from(e.target.files || []);
    if (!chosen.length) return;
    setNewDocs((prev) => {
      const projectedUsed = getProjectedActiveDocCount(prev, removedDocIds);
      const remaining = MAX_ADDITIONAL_DOCS - projectedUsed;
      return [
        ...prev,
        ...chosen.slice(0, Math.max(0, remaining)).map((file) => ({
          file,
          targetField: "additional_documents",
          replacesDocId: null,
        })),
      ];
    });
    if (addDocsInputRef.current) addDocsInputRef.current.value = "";
  };

  const onSwitchDocFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !switchTarget) {
      if (switchInputRef.current) switchInputRef.current.value = "";
      setSwitchTarget(null);
      return;
    }

    upsertPendingDoc({
      file,
      targetField: switchTarget.targetField,
      replacesDocId: switchTarget.docId,
    });

    if (switchInputRef.current) switchInputRef.current.value = "";
    setSwitchTarget(null);
  };

  const startSwitchExistingDoc = (doc) => {
    const targetField = DOC_DB_TO_UPLOAD[doc?.field_name];
    if (!targetField) return;
    setSwitchTarget({ docId: Number(doc.id), targetField });
    switchInputRef.current?.click();
  };

  const startUploadForTarget = (targetField) => {
    setSwitchTarget({ docId: null, targetField });
    switchInputRef.current?.click();
  };

  const removeNewDoc = (idx) => setNewDocs((p) => p.filter((_, i) => i !== idx));

  const openSelectedFilePreview = (fileObj) => {
    const file = fileObj?.file || fileObj;
    if (!file) return;
    const objectUrl = URL.createObjectURL(file);
    window.open(objectUrl, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
  };

  const openExistingDoc = (doc) => {
    if (!doc?.id) return;
    const url = getBusinessApplicationDocumentUrl(id, doc.id);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const toggleRemoveExisting = (docId) => {
    setNewDocs((prev) => prev.filter((item) => Number(item.replacesDocId) !== Number(docId)));
    setRemovedDocIds((prev) =>
      prev.includes(docId) ? prev.filter((x) => x !== docId) : [...prev, docId]
    );
  };

  const projectedActiveDocCount = getProjectedActiveDocCount();
  const remainingSlots = MAX_ADDITIONAL_DOCS - projectedActiveDocCount;
  const permitDoc = existingDocs.find((doc) => doc.field_name === "dti_sec_permit");
  const additionalDocs = existingDocs.filter((doc) => doc.field_name === "additional_document");
  const pendingReplacementByDocId = useMemo(() => {
    const map = new Map();
    newDocs.forEach((item, idx) => {
      if (item.replacesDocId) map.set(Number(item.replacesDocId), { ...item, index: idx });
    });
    return map;
  }, [newDocs]);
  const pendingRequiredUploads = useMemo(() => {
    const map = new Map();
    newDocs.forEach((item, idx) => {
      if (!item.replacesDocId && item.targetField !== "additional_documents") {
        map.set(item.targetField, { ...item, index: idx });
      }
    });
    return map;
  }, [newDocs]);
  const manualNewDocs = newDocs
    .map((item, idx) => ({ ...item, index: idx }))
    .filter((item) => !item.replacesDocId && item.targetField === "additional_documents");
  const displayedOwnerEmail = maskEmail(form.ownerEmail || "");
  const displayedOwnerPhone = maskPhone(form.ownerPhone || "");

  const collectChangeSummary = useCallback(() => {
    if (!app) return [];

    const lines = [];
    const originalOwnerName = [app.owner_first_name, app.owner_last_name].filter(Boolean).join(" ");
    const fieldPairs = [
      ["ownerFullName", normalizeValue(originalOwnerName), normalizeValue(form.ownerFullName)],
      ["ownerEmail", normalizeValue(app.owner_email), normalizeValue(form.ownerEmail)],
      ["ownerPhone", normalizeValue(app.owner_phone), normalizeValue(form.ownerPhone)],
      ["businessName", normalizeValue(app.business_name), normalizeValue(form.businessName)],
      ["registrationNumber", normalizeValue(app.registration_number), normalizeValue(form.registrationNumber)],
      ["taxId", normalizeValue(app.tax_id), normalizeValue(form.taxId)],
      ["businessEmail", normalizeValue(app.business_email), normalizeValue(form.businessEmail)],
      ["businessPhone", normalizeValue(app.business_phone), normalizeValue(form.businessPhone)],
      ["addressLine1", normalizeValue(app.address_line1), normalizeValue(form.addressLine1)],
      ["addressLine2", normalizeValue(app.address_line2), normalizeValue(form.addressLine2)],
      ["city", normalizeValue(app.city), normalizeValue(form.city)],
      ["province", normalizeValue(app.province), normalizeValue(form.province)],
      ["postalCode", normalizeValue(app.postal_code), normalizeValue(form.postalCode)],
    ];

    fieldPairs.forEach(([key, oldValue, newValue]) => {
      if (oldValue !== newValue) {
        lines.push(`${FIELD_LABELS[key]}: ${oldValue || "(empty)"} -> ${newValue || "(empty)"}`);
      }
    });

    const switchedDocs = newDocs.filter((item) => item.replacesDocId);
    switchedDocs.forEach((item) => {
      const oldDoc = existingDocs.find((doc) => Number(doc.id) === Number(item.replacesDocId));
      const oldName = oldDoc ? getExistingDocName(oldDoc) : "(unknown file)";
      const fieldName = oldDoc?.field_name || DOC_FIELD_TO_DB[item.targetField] || "document";
      lines.push(`Switched ${DOC_LABELS[fieldName] || fieldName}: ${oldName} -> ${item.file.name}`);
    });

    const removedAdditional = removedDocIds
      .map((idVal) => existingDocs.find((doc) => Number(doc.id) === Number(idVal)))
      .filter((doc) => doc && doc.field_name === "additional_document" && !switchedDocs.some((item) => Number(item.replacesDocId) === Number(doc.id)));
    removedAdditional.forEach((doc) => {
      lines.push(`Removed additional document: ${getExistingDocName(doc)}`);
    });

    const addedAdditional = manualNewDocs.filter((item) => item.targetField === "additional_documents");
    addedAdditional.forEach((item) => {
      lines.push(`Added additional document: ${item.file.name}`);
    });

    if (form.applicantNotes.trim()) {
      lines.push("Added/updated applicant notes");
    }

    return lines;
  }, [app, form, newDocs, existingDocs, removedDocIds, manualNewDocs]);
  const hasFieldChanges = useMemo(() => {
    if (!app) return false;

    const originalOwnerName = [app.owner_first_name, app.owner_last_name].filter(Boolean).join(" ");
    return (
      normalizeValue(form.ownerFullName) !== normalizeValue(originalOwnerName) ||
      normalizeValue(form.ownerEmail) !== normalizeValue(app.owner_email) ||
      normalizeValue(form.ownerPhone) !== normalizeValue(app.owner_phone) ||
      normalizeValue(form.businessName) !== normalizeValue(app.business_name) ||
      normalizeValue(form.registrationNumber) !== normalizeValue(app.registration_number) ||
      normalizeValue(form.taxId) !== normalizeValue(app.tax_id) ||
      normalizeValue(form.businessEmail) !== normalizeValue(app.business_email) ||
      normalizeValue(form.businessPhone) !== normalizeValue(app.business_phone) ||
      normalizeValue(form.addressLine1) !== normalizeValue(app.address_line1) ||
      normalizeValue(form.addressLine2) !== normalizeValue(app.address_line2) ||
      normalizeValue(form.city) !== normalizeValue(app.city) ||
      normalizeValue(form.province) !== normalizeValue(app.province) ||
      normalizeValue(form.postalCode) !== normalizeValue(app.postal_code)
    );
  }, [app, form]);
  const hasDocumentChanges = newDocs.length > 0 || removedDocIds.length > 0;
  const isDirty = hasFieldChanges || hasDocumentChanges || Boolean(form.applicantNotes.trim()) || form.agree;

  useEffect(() => {
    if (!isDirty || submitting) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty, submitting]);

  /* ── Validation ── */
  const validate = useCallback(() => {
    const errors = [];
    if (!NAME_RE.test(form.ownerFullName.trim())) errors.push("Owner name must be letters only (2–80 characters).");
    if (!EMAIL_RE.test(form.ownerEmail.trim())) errors.push("Owner email is invalid.");
    if (!PHONE_RE.test(form.ownerPhone.trim())) errors.push("Owner phone must be 09XXXXXXXXX.");
    if (form.businessName.trim().length < 2) errors.push("Business name must be at least 2 characters.");
    const bEmail = form.businessEmail.trim();
    const bPhone = form.businessPhone.trim();
    if (bEmail && !EMAIL_RE.test(bEmail)) errors.push("Business email is invalid.");
    if (bPhone && !PHONE_RE.test(bPhone)) errors.push("Business phone must be 09XXXXXXXXX.");
    if (form.addressLine1.trim().length < 4) errors.push("Address line 1 is required (min 4 characters).");
    if (!CITY_RE.test(form.city.trim())) errors.push("City must be letters only (2–60 characters).");
    if (!CITY_RE.test(form.province.trim())) errors.push("Province must be letters only (2–60 characters).");
    if (!POSTAL_RE.test(form.postalCode.trim())) errors.push("Postal code must be 4–6 digits.");
    if (!form.agree) errors.push(BUSINESS_APPLICATION_TERMS_REQUIRED_MESSAGE);
    for (let i = 0; i < newDocs.length; i++) {
      const f = newDocs[i]?.file;
      const maxMb = getMaxFileMbForTarget(newDocs[i]?.targetField);
      if (!ALLOWED_TYPES.includes(f.type)) errors.push(`New doc #${i + 1}: must be PDF, JPG, or PNG.`);
      if (f.size > maxMb * 1024 * 1024) errors.push(`New doc #${i + 1}: exceeds ${maxMb}MB.`);
    }

    // If user marks an existing required doc for removal, they must provide a replacement target.
    const requiredDbFields = REQUIRED_DOC_FIELDS;
    const replacementTargets = new Set(
      newDocs.map((d) => DOC_FIELD_TO_DB[d.targetField]).filter((v) => requiredDbFields.includes(v))
    );
    for (const removedId of removedDocIds) {
      const removedDoc = existingDocs.find((d) => Number(d.id) === Number(removedId));
      if (!removedDoc) continue;
      if (requiredDbFields.includes(removedDoc.field_name) && !replacementTargets.has(removedDoc.field_name)) {
        errors.push(`You removed ${DOC_TARGET_OPTIONS.find((o) => DOC_FIELD_TO_DB[o.value] === removedDoc.field_name)?.label || removedDoc.field_name} but did not upload a replacement for it.`);
      }
    }

    if (!hasFieldChanges && !hasDocumentChanges) {
      errors.push("No changes detected. Update at least one field or document before resubmitting.");
    }

    return errors;
  }, [form, newDocs, existingDocs, removedDocIds, hasFieldChanges, hasDocumentChanges]);

  const handleLeavePage = useCallback(async (event, target = "/account/notifications") => {
    if (!isDirty || submitting) return;
    event.preventDefault();

    const confirmLeave = await Swal.fire({
      ...swalBase,
      icon: "warning",
      title: "Discard unsaved changes?",
      html: "Your updates have not been resubmitted yet. Leaving now will discard the changes currently on this page.",
      showCancelButton: true,
      confirmButtonText: "Leave Page",
      cancelButtonText: "Stay Here",
    });

    if (confirmLeave.isConfirmed) {
      navigate(target);
    }
  }, [isDirty, navigate, submitting, swalBase]);

  /* ── Submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const errors = validate();
    if (errors.length) {
      await Swal.fire({
        ...swalBase, icon: "error", title: "Fix the following",
        html: `<ul style="text-align:left;margin:0;padding-left:18px;">${errors.map((x) => `<li>${x}</li>`).join("")}</ul>`,
        confirmButtonText: "OK",
      });
      return;
    }

    const changeSummary = collectChangeSummary();
    const confirmResubmit = await Swal.fire({
      ...swalBase,
      icon: "question",
      title: "Confirm resubmission",
      html: changeSummary.length
        ? `<div style="text-align:left;"><p style="margin:0 0 8px 0;font-weight:600;">What changed:</p><ul style="margin:0;padding-left:18px;">${changeSummary
            .map((line) => `<li>${line}</li>`)
            .join("")}</ul></div>`
        : "No detectable changes summary found.",
      showCancelButton: true,
      confirmButtonText: "Proceed",
      cancelButtonText: "Cancel",
    });

    if (!confirmResubmit.isConfirmed) {
      return;
    }

    setSubmitting(true);
    Swal.fire({
      ...swalBase, title: "Resubmitting…",
      html: "Uploading documents and sending your updated application",
      allowOutsideClick: false, allowEscapeKey: false, showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const fd = new FormData();
      const documentAssets = [];
      // Split ownerFullName into first/last
      const nameParts = form.ownerFullName.trim().split(/\s+/);
      fd.append("ownerFirstName", nameParts[0] || "");
      fd.append("ownerLastName", nameParts.slice(1).join(" ") || nameParts[0] || "");
      fd.append("ownerEmail", form.ownerEmail.trim());
      fd.append("ownerPhone", form.ownerPhone.trim());
      fd.append("businessName", form.businessName.trim());
      fd.append("registrationNumber", form.registrationNumber.trim());
      fd.append("taxId", form.taxId.trim());
      fd.append("businessEmail", form.businessEmail.trim());
      fd.append("businessPhone", form.businessPhone.trim());
      fd.append("addressLine1", form.addressLine1.trim());
      fd.append("addressLine2", form.addressLine2.trim());
      fd.append("city", form.city.trim());
      fd.append("province", form.province.trim());
      fd.append("postalCode", form.postalCode.trim());
      fd.append("agree", form.agree ? "true" : "false");
      if (form.applicantNotes.trim()) fd.append("applicantNotes", form.applicantNotes.trim());

      for (const docItem of newDocs) {
        const targetField = docItem.targetField || "additional_documents";
        const uploadedDoc = await uploadBusinessApplicationDocumentAsset(docItem.file, {
          applicationId: id,
          fieldName: targetField,
        });

        documentAssets.push({
          assetId: uploadedDoc?.assetId,
          fieldName: DOC_FIELD_TO_DB[targetField] || "additional_document",
          ...(docItem.replacesDocId ? { replacedDocumentId: Number(docItem.replacesDocId) } : {}),
        });
      }

      if (documentAssets.length) {
        fd.append("documentAssets", JSON.stringify(documentAssets));
      }

      fd.append("removedDocumentIds", JSON.stringify(removedDocIds.map((v) => Number(v)).filter((v) => Number.isInteger(v) && v > 0)));

      await resubmitApplication(id, fd);

      await Swal.fire({
        ...swalBase, icon: "success", title: "Application resubmitted",
        html: "Your updated application has been sent for review.",
        timer: 1800, showConfirmButton: false,
      });
      navigate("/account/notifications", { replace: true });
    } catch (err) {
      Swal.close();
      const errors = Array.isArray(err?.response?.data?.errors) ? err.response.data.errors : [];
      const msg = err?.response?.data?.msg || err?.message || "Please try again.";
      const html = errors.length
        ? `<div style="text-align:left;"><p style="margin:0 0 8px 0;">${msg}</p><ul style="margin:0;padding-left:18px;">${errors.map((item) => `<li>${item}</li>`).join("")}</ul></div>`
        : msg;
      await Swal.fire({ ...swalBase, icon: "error", title: "Resubmission failed", html, confirmButtonText: "OK" });
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Non-ready states ── */
  if (pageState === "loading") {
    return <FormSkeleton label="Loading business application" />;
  }
  if (pageState === "notfound") {
    return (
      <div className="mx-auto max-w-md py-20 text-center">
        <h2 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200">Application Not Found</h2>
        <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
          {loadMessage || "This application doesn't exist or is no longer editable."}
        </p>
        <Link to="/account/notifications" className="mt-4 inline-block text-sm text-blue-600 underline hover:text-blue-800 dark:text-blue-400">
          Back to Notifications
        </Link>
      </div>
    );
  }
  if (pageState === "error") {
    return (
      <div className="mx-auto max-w-md py-20 text-center">
        <h2 className="text-lg font-semibold text-red-600 dark:text-red-400">Failed to load</h2>
        <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
          {loadMessage || "Something went wrong. Please try again later."}
        </p>
        <Link to="/account/notifications" className="mt-4 inline-block text-sm text-blue-600 underline hover:text-blue-800 dark:text-blue-400">
          Back to Notifications
        </Link>
      </div>
    );
  }

  const reviewerNotes = app?.admin_notes || app?.reviewer_notes || app?.requested_info_message;

  return (
    <div className="min-h-screen bg-white px-4 py-10 text-neutral-900 dark:bg-neutral-900 dark:text-neutral-50">
      <div className="mx-auto w-full max-w-3xl">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/account/notifications"
            onClick={(event) => handleLeavePage(event)}
            className="mb-3 inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-800 dark:text-neutral-400 dark:hover:text-neutral-200 transition"
          >
            <ArrowLeft size={14} /> Back to Notifications
          </Link>
          <p className={sectionTitle}>RESUBMISSION</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight">UPDATE YOUR APPLICATION</h1>
          <p className="mt-3 text-sm text-neutral-600 dark:text-neutral-300">
            The reviewer has requested additional information. Please update the relevant fields and resubmit.
          </p>

          {reviewerNotes && (
            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800/50 dark:bg-amber-950/40">
              <p className="text-xs font-semibold tracking-wide text-amber-700 dark:text-amber-400 mb-1">REVIEWER NOTES</p>
              <p className="text-sm text-amber-900 dark:text-amber-200 whitespace-pre-wrap">{reviewerNotes}</p>
            </div>
          )}
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
          <div className="h-1 w-full rounded-t-2xl bg-blue-600 dark:bg-blue-500" />

          <form onSubmit={handleSubmit} className="p-6 md:p-8">
            <div className="grid gap-6">

              {/* ── OWNER ── */}
              <div>
                <div className="mb-4">
                  <div className={sectionTitle}>OWNER</div>
                  <div className="mt-1 flex items-start justify-between gap-4">
                    <div className="text-lg font-semibold">Using your account details</div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-400">
                      Required fields marked <span className="text-red-600 dark:text-red-500">*</span>
                    </div>
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <label className={labelBase}>NAME <Req /></label>
                    <input name="ownerFullName" value={form.ownerFullName}
                      className={`${inputBase} bg-neutral-50 dark:bg-neutral-800/70`} placeholder="Full name" disabled={submitting}
                      readOnly required minLength={2} maxLength={80} />
                  </div>
                  <div>
                    <label className={labelBase}>EMAIL <Req /></label>
                    <input name="ownerEmail" value={displayedOwnerEmail}
                      className={`${inputBase} bg-neutral-50 dark:bg-neutral-800/70`} placeholder="owner@business.com" type="text"
                      disabled={submitting} readOnly maxLength={120} />
                  </div>
                  <div>
                    <label className={labelBase}>PHONE <Req /></label>
                    <input name="ownerPhone" value={displayedOwnerPhone}
                      className={`${inputBase} bg-neutral-50 dark:bg-neutral-800/70`} placeholder="09XXXXXXXXX" disabled={submitting}
                      readOnly inputMode="numeric" maxLength={11} />
                  </div>
                </div>
              </div>

              <div className="border-t border-neutral-200 pt-6 dark:border-neutral-800" />

              {/* ── BUSINESS ── */}
              <div>
                <div className="mb-4">
                  <div className={sectionTitle}>BUSINESS</div>
                  <div className="mt-1 text-lg font-semibold">Business information</div>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="md:col-span-2">
                    <label className={labelBase}>BUSINESS NAME <Req /></label>
                    <input name="businessName" value={form.businessName} onChange={onChange}
                      className={inputBase} placeholder="Your business name" disabled={submitting}
                      required minLength={2} maxLength={120} />
                  </div>
                  <div>
                    <label className={labelBase}>REGISTRATION NO.</label>
                    <input name="registrationNumber" value={form.registrationNumber} onChange={onChange}
                      className={inputBase} placeholder="DTI/SEC/Other" disabled={submitting} maxLength={40} />
                  </div>
                  <div>
                    <label className={labelBase}>TAX ID / TIN</label>
                    <input name="taxId" value={form.taxId} onChange={onChange}
                      className={inputBase} placeholder="Numbers and dashes" disabled={submitting}
                      maxLength={15} inputMode="numeric" />
                  </div>
                  <div>
                    <label className={labelBase}>BUSINESS EMAIL</label>
                    <input name="businessEmail" value={form.businessEmail} onChange={onChange}
                      className={inputBase} placeholder="support@store.com" type="email"
                      disabled={submitting} maxLength={120} />
                  </div>
                  <div>
                    <label className={labelBase}>BUSINESS PHONE</label>
                    <input name="businessPhone" value={form.businessPhone} onChange={onChange}
                      className={inputBase} placeholder="09XXXXXXXXX" disabled={submitting}
                      inputMode="numeric" maxLength={11} />
                  </div>
                </div>
              </div>

              <div className="border-t border-neutral-200 pt-6 dark:border-neutral-800" />

              {/* ── ADDRESS ── */}
              <div>
                <div className="mb-4">
                  <div className={sectionTitle}>ADDRESS</div>
                  <div className="mt-1 text-lg font-semibold">Business address</div>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="md:col-span-2">
                    <label className={labelBase}>ADDRESS LINE 1 <Req /></label>
                    <input name="addressLine1" value={form.addressLine1} onChange={onChange}
                      className={inputBase} placeholder="Street, building, unit" disabled={submitting}
                      required minLength={4} maxLength={140} />
                  </div>
                  <div>
                    <label className={labelBase}>ADDRESS LINE 2</label>
                    <input name="addressLine2" value={form.addressLine2} onChange={onChange}
                      className={inputBase} placeholder="Optional" disabled={submitting} maxLength={140} />
                  </div>
                  <div>
                    <label className={labelBase}>CITY <Req /></label>
                    <input name="city" value={form.city} onChange={onChange}
                      className={inputBase} placeholder="City" disabled={submitting}
                      required minLength={2} maxLength={60} />
                  </div>
                  <div>
                    <label className={labelBase}>PROVINCE <Req /></label>
                    <input name="province" value={form.province} onChange={onChange}
                      className={inputBase} placeholder="Province" disabled={submitting}
                      required minLength={2} maxLength={60} />
                  </div>
                  <div>
                    <label className={labelBase}>POSTAL CODE <Req /></label>
                    <input name="postalCode" value={form.postalCode} onChange={onChange}
                      className={inputBase} placeholder="4–6 digits" disabled={submitting}
                      required inputMode="numeric" maxLength={6} />
                  </div>
                </div>
              </div>

              <div className="border-t border-neutral-200 pt-6 dark:border-neutral-800" />

              {/* ── DOCUMENTS ── */}
              <div>
                <div className="mb-4">
                  <div className={sectionTitle}>DOCUMENTS</div>
                  <div className="mt-1 text-lg font-semibold">Review & update documents</div>
                  <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">
                    Required documents and additional documents are separated below. Use Switch on a specific file when you want the new upload to replace that exact document.
                  </p>
                </div>

                <input
                  ref={switchInputRef}
                  type="file"
                  accept=".pdf,image/jpeg,image/png"
                  onChange={onSwitchDocFileChange}
                  disabled={submitting}
                  className="hidden"
                />

                {permitDoc ? (
                  <div className="mb-5 space-y-2">
                    <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">PERMIT DOCUMENT</p>
                    {(() => {
                      const doc = permitDoc;
                      const pendingReplacement = pendingReplacementByDocId.get(Number(doc.id));
                      const docName = getExistingDocName(doc);
                      const docType = getExistingDocType(doc);
                      const canPreview = Boolean(doc?.id);
                      const showImageThumb = isImageMime(doc?.content_type);
                      const thumbSrc = canPreview ? getBusinessApplicationDocumentUrl(id, doc.id) : "";
                      return (
                        <div className="rounded-xl border border-neutral-200 p-3 dark:border-neutral-800">
                          <div className="flex items-center gap-3">
                            {showImageThumb ? (
                              <img
                                src={thumbSrc}
                                alt={docName}
                                className="h-10 w-10 shrink-0 rounded-lg border border-neutral-200 object-cover dark:border-neutral-700"
                              />
                            ) : (
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-neutral-200 bg-neutral-50 text-xs font-semibold text-neutral-500 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-400">
                                {docType === "PDF" ? "PDF" : "DOC"}
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-medium text-neutral-900 dark:text-neutral-100">{docName}</div>
                              <div className="text-xs text-neutral-500 dark:text-neutral-400">
                                {DOC_LABELS[doc.field_name] || doc.field_name} • {docType}
                                {doc?.size_bytes ? ` • ${formatFileSize(doc.size_bytes)}` : ""}
                              </div>
                              {pendingReplacement && (
                                <div className="mt-1 text-[11px] font-medium text-amber-600 dark:text-amber-400">
                                  Will switch to: {pendingReplacement.file.name}
                                </div>
                              )}
                            </div>
                            {canPreview && (
                              <button
                                type="button"
                                onClick={() => openExistingDoc(doc)}
                                className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold border border-neutral-300 text-neutral-700 bg-white hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700"
                              >
                                View
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => startSwitchExistingDoc(doc)}
                              className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50"
                            >
                              Switch
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="mb-5 space-y-2">
                    <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">PERMIT DOCUMENT</p>
                    <div className="rounded-xl border border-dashed border-amber-200 bg-amber-50/60 p-4 dark:border-amber-900/40 dark:bg-amber-950/20">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-amber-900 dark:text-amber-200">DTI/SEC Permit is currently missing</div>
                          <div className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                            Upload a permit file here so the resubmission clearly restores the required permit document.
                          </div>
                          {pendingRequiredUploads.get("permit") && (
                            <div className="mt-1 text-[11px] font-medium text-amber-700 dark:text-amber-300">
                              Pending permit upload: {pendingRequiredUploads.get("permit").file.name}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => startUploadForTarget("permit")}
                          className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50"
                        >
                          Upload Permit
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mb-5 space-y-2">
                  <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">ADDITIONAL DOCUMENTS</p>
                  {additionalDocs.length > 0 ? additionalDocs.map((doc) => {
                    const isRemoved = removedDocIds.includes(doc.id);
                    const pendingReplacement = pendingReplacementByDocId.get(Number(doc.id));
                    const docName = getExistingDocName(doc);
                    const docType = getExistingDocType(doc);
                    const canPreview = Boolean(doc?.id);
                    const showImageThumb = isImageMime(doc?.content_type) && !isRemoved;
                    const thumbSrc = canPreview ? getBusinessApplicationDocumentUrl(id, doc.id) : "";
                    return (
                      <div
                        key={doc.id}
                        className={[
                          "rounded-xl border p-3 transition",
                          isRemoved
                            ? "border-red-200 bg-red-50/50 dark:border-red-900/40 dark:bg-red-950/20 opacity-70"
                            : pendingReplacement
                              ? "border-amber-200 bg-amber-50/40 dark:border-amber-900/40 dark:bg-amber-950/20"
                              : "border-neutral-200 dark:border-neutral-800",
                        ].join(" ")}
                      >
                        <div className="flex items-center gap-3">
                          {showImageThumb ? (
                            <img
                              src={thumbSrc}
                              alt={docName}
                              className="h-10 w-10 shrink-0 rounded-lg border border-neutral-200 object-cover dark:border-neutral-700"
                            />
                          ) : (
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-neutral-200 bg-neutral-50 text-xs font-semibold text-neutral-500 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-400">
                              {docType === "PDF" ? "PDF" : "DOC"}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className={`truncate text-sm font-medium ${isRemoved ? "line-through text-neutral-400" : "text-neutral-900 dark:text-neutral-100"}`}>
                              {docName}
                            </div>
                            <div className="text-xs text-neutral-500 dark:text-neutral-400">
                              {docType}
                              {doc?.size_bytes ? ` • ${formatFileSize(doc.size_bytes)}` : ""}
                            </div>
                            {pendingReplacement && !isRemoved && (
                              <div className="mt-1 text-[11px] font-medium text-amber-600 dark:text-amber-400">
                                Will switch to: {pendingReplacement.file.name}
                              </div>
                            )}
                            {isRemoved && (
                              <div className="mt-1 text-[11px] font-medium text-red-600 dark:text-red-400">
                                Marked for removal on resubmit
                              </div>
                            )}
                          </div>
                          {canPreview && !isRemoved && (
                            <button
                              type="button"
                              onClick={() => openExistingDoc(doc)}
                              className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold border border-neutral-300 text-neutral-700 bg-white hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700"
                            >
                              View
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => startSwitchExistingDoc(doc)}
                            disabled={isRemoved}
                            className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50"
                          >
                            Switch
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleRemoveExisting(doc.id)}
                            className={[
                              "shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold transition",
                              isRemoved
                                ? "bg-neutral-100 text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-300"
                                : "bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-950/30 dark:text-red-400 dark:hover:bg-red-950/50",
                            ].join(" ")}
                          >
                            {isRemoved ? "Undo" : "Remove"}
                          </button>
                        </div>
                      </div>
                    );
                  }) : (
                    <div className="rounded-xl border border-dashed border-neutral-200 p-4 text-sm text-neutral-500 dark:border-neutral-800 dark:text-neutral-400">
                      No additional documents uploaded yet.
                    </div>
                  )}
                </div>

                {/* New documents */}
                {manualNewDocs.length > 0 && (
                  <div className="mb-4 space-y-2">
                    <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">NEW ADDITIONAL DOCUMENTS</p>
                    {manualNewDocs.map((docItem) => (
                      <div key={`${docItem.file.name}-${docItem.file.size}-${docItem.index}`}
                        className="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50/30 p-3 dark:border-blue-900/40 dark:bg-blue-950/20">
                        {isImageType(docItem.file) ? (
                          <img src={URL.createObjectURL(docItem.file)} alt={docItem.file.name}
                            className="h-10 w-10 shrink-0 rounded-lg border border-neutral-200 object-cover dark:border-neutral-700" />
                        ) : (
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-neutral-200 bg-neutral-50 text-xs font-semibold text-neutral-500 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-400">
                            PDF
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-neutral-900 dark:text-neutral-100">{docItem.file.name}</div>
                          <div className="text-xs text-neutral-500 dark:text-neutral-400">
                            {formatFileSize(docItem.file.size)}
                            {!ALLOWED_TYPES.includes(docItem.file.type) && <span className="ml-2 text-red-500">Unsupported</span>}
                            {docItem.file.size > getMaxFileMbForTarget(docItem.targetField) * 1024 * 1024 && <span className="ml-2 text-red-500">Too large</span>}
                          </div>
                          <div className="mt-2 text-[11px] font-semibold text-blue-700 dark:text-blue-300">Will be submitted as an additional document</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => openSelectedFilePreview(docItem)}
                          disabled={submitting}
                          className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold border border-neutral-300 text-neutral-700 bg-white hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700"
                        >
                          Preview
                        </button>
                        <button type="button" onClick={() => removeNewDoc(docItem.index)} disabled={submitting}
                          className="shrink-0 rounded-lg p-1.5 text-neutral-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add more */}
                {remainingSlots > 0 && (
                  <label className="group flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-neutral-200 p-4 transition hover:border-neutral-400 hover:bg-neutral-50 dark:border-neutral-700 dark:hover:border-neutral-500 dark:hover:bg-neutral-800/50">
                    <input ref={addDocsInputRef} type="file" accept=".pdf,image/jpeg,image/png" multiple
                      onChange={onNewDocsChange} disabled={submitting} className="hidden" />
                    <span className="text-xs font-semibold tracking-wide text-neutral-500 group-hover:text-neutral-700 dark:text-neutral-400 dark:group-hover:text-neutral-200">
                      + ADD ADDITIONAL DOCUMENT{remainingSlots > 1 ? "S" : ""} ({remainingSlots} remaining)
                    </span>
                  </label>
                )}

                {remainingSlots <= 0 && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-300">
                    You have reached the maximum active document count. Switch an existing file instead of adding a new one if you want to keep the total within the limit.
                  </div>
                )}
              </div>

              <div className="border-t border-neutral-200 pt-6 dark:border-neutral-800" />

              {/* ── NOTES + CONSENT ── */}
              <div className="grid gap-4 md:grid-cols-3">
                <div className="md:col-span-2">
                  <label className={labelBase}>APPLICANT NOTES (Optional)</label>
                  <textarea name="applicantNotes" value={form.applicantNotes} onChange={onChange}
                    className={`${inputBase} min-h-[100px] resize-none`}
                    placeholder="Explain what you've updated or any additional context for the reviewer..."
                    disabled={submitting} maxLength={600} />
                  <div className="mt-1 text-[11px] text-neutral-400">{form.applicantNotes.length}/600</div>
                </div>

                <div className="md:col-span-1">
                  <div className="rounded-2xl border border-neutral-200 p-4 dark:border-neutral-800">
                    <div className="text-sm font-semibold">{BUSINESS_APPLICATION_TERMS_TITLE}</div>
                    <p className="mt-2 text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">{BUSINESS_APPLICATION_TERMS_BODY}</p>

                    <label className="mt-4 flex cursor-pointer items-start gap-3">
                      <input
                        type="checkbox"
                        name="agree"
                        checked={form.agree}
                        onChange={onChange}
                        disabled={submitting}
                        className="mt-1 h-4 w-4 rounded border-neutral-300 text-neutral-900 dark:border-neutral-700"
                        required
                      />
                      <span className="text-xs leading-relaxed text-neutral-600 dark:text-neutral-300">
                        {BUSINESS_APPLICATION_TERMS_ACKNOWLEDGEMENT_LABEL} <span className="text-red-600 dark:text-red-500">*</span>
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* ── ACTIONS ── */}
              <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
                <Link to="/account/notifications"
                  onClick={(event) => handleLeavePage(event)}
                  className="text-sm text-neutral-600 underline underline-offset-4 hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-white">
                  Cancel
                </Link>
                <button type="submit" disabled={submitting}
                  className="rounded-xl bg-blue-600 px-6 py-3 text-xs font-semibold tracking-[0.25em] text-white transition hover:bg-blue-700 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60">
                  {submitting ? "RESUBMITTING…" : "RESUBMIT APPLICATION"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
